import type {
  TicketMessageKind,
  TicketPriority,
  TicketResponseTargets,
  TicketEvent,
  TicketStaffListItem,
  TicketTraceStatus,
  TicketUrgency,
} from '@traceio-org/platform-contracts';
import {
  TICKET_PRIORITIES,
  TICKET_STAFF_REOPEN_TARGETS,
  TICKET_TRACE_STATUSES,
} from '@traceio-org/platform-contracts';
import { plural } from '../../format';

/** Trace's statuses in the words of the queue (spec: Triage … Duplicate), in queue order. */
export const TRACE_STATUS_LABELS: Readonly<Record<TicketTraceStatus, string>> = {
  triage: 'Triage',
  todo: 'Todo',
  in_progress: 'In progress',
  waiting_on_customer: 'Waiting on customer',
  fix_in_progress: 'Fix in progress',
  done: 'Done',
  cancelled: 'Cancelled',
  duplicate: 'Duplicate',
};

export const PRIORITY_LABELS: Readonly<Record<TicketPriority, string>> = {
  urgent: 'Urgent',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  none: 'No priority',
};

export const URGENCY_LABELS: Readonly<Record<TicketUrgency, string>> = {
  blocking: 'Blocking me',
  soon: 'Soon',
  whenever: 'Whenever',
};

export const STATUS_OPTIONS = TICKET_TRACE_STATUSES.map((value) => ({
  value,
  label: TRACE_STATUS_LABELS[value],
}));
export const PRIORITY_OPTIONS = TICKET_PRIORITIES.map((value) => ({ value, label: PRIORITY_LABELS[value] }));

/** Who reads a message, as its tag says (Reply · customer sees, Trace note · Only Trace). */
export const MESSAGE_KIND_TAGS: Readonly<Record<TicketMessageKind, string>> = {
  reply: 'Reply · customer sees',
  customer_note: "Customer note · the customer's help desk",
  trace_note: 'Trace note · Only Trace',
  system: 'Event',
};

/** The statuses that close a ticket: no response clock, no hand back. */
export const CLOSED_STATUSES: readonly TicketTraceStatus[] = ['done', 'cancelled', 'duplicate'];

/**
 * Whether staff who manage tickets may move a ticket from `from` to `to`, as platform-api's set_trace_status rule
 * decides (the menu disables the rest, so a click never ends in a 409): Triage is only entered by an escalation; a
 * closed ticket moves between closed statuses or reopens into TICKET_STAFF_REOPEN_TARGETS (Todo, In progress) only.
 */
export function staffStatusMoveAllowed(from: TicketTraceStatus, to: TicketTraceStatus): boolean {
  if (to === from) return true;
  if (to === 'triage') return false;
  if (!CLOSED_STATUSES.includes(from)) return true;
  return (
    CLOSED_STATUSES.includes(to) || (TICKET_STAFF_REOPEN_TARGETS as readonly TicketTraceStatus[]).includes(to)
  );
}

/** A span in words at the coarsest useful unit: 25 min, 3 h, 2 d. */
export function formatSpan(milliseconds: number): string {
  const minutes = Math.max(1, Math.round(Math.abs(milliseconds) / 60_000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h`;
  return `${Math.round(hours / 24)} d`;
}

export type DueTone = 'late' | 'soon' | 'ok' | 'replied';

/** Within this of its due time a first reply is shown as coming up soon (amber). */
const SOON_MS = 60 * 60_000;

/**
 * The response-target line of a queue row: Replied once Trace answered, Overdue <span> in red once breached (the
 * queue's overdue flag, which platform-api computes, decides), Reply in <span> otherwise, nothing without a target.
 */
export function dueOf(
  item: Pick<TicketStaffListItem, 'firstTraceReplyAt' | 'responseDueAt' | 'overdue'>,
  now: number,
): { tone: DueTone; text: string } | null {
  if (item.firstTraceReplyAt) return { tone: 'replied', text: 'Replied' };
  if (!item.responseDueAt) return null;
  const left = Date.parse(item.responseDueAt) - now;
  if (item.overdue || left <= 0) return { tone: 'late', text: `Overdue ${formatSpan(left)}` };
  return { tone: left <= SOON_MS ? 'soon' : 'ok', text: `Reply in ${formatSpan(left)}` };
}

/** How long ago, compactly (the queue's age column). */
export function ageOf(iso: string, now: number): string {
  return formatSpan(now - Date.parse(iso));
}

/** A response target in words: 1 h, 4 h, 1 business day. */
export function describeTarget(target: TicketResponseTargets[keyof TicketResponseTargets]): string {
  if (!target) return 'No target';
  if ('hours' in target) return `${target.hours} h`;
  return plural(target.businessDays, 'business day', 'business days');
}

/** Linear's issue URL: https://linear.app/<workspace>/issue/<KEY-123>[/slug]. */
const LINEAR_ISSUE = /^https:\/\/linear\.app\/[^/]+\/issue\/([A-Z][A-Z0-9]{0,9}-[1-9][0-9]{0,9})(?:[/?#]|$)/;

/** The issue key of a Linear issue URL (TE-812), or null when it is not one. */
export function linearKeyOf(url: string): string | null {
  return LINEAR_ISSUE.exec(url.trim())?.[1] ?? null;
}

function personName(value: unknown): string | null {
  const v = value as { name?: unknown } | null;
  return v && typeof v.name === 'string' ? v.name : null;
}

function labelList(value: unknown): string {
  return Array.isArray(value) && value.length > 0 ? value.join(', ') : 'none';
}

function statusWord(value: unknown): string {
  return typeof value === 'string' && value in TRACE_STATUS_LABELS
    ? TRACE_STATUS_LABELS[value as TicketTraceStatus]
    : 'none';
}

function priorityWord(value: unknown): string {
  return typeof value === 'string' && value in PRIORITY_LABELS
    ? PRIORITY_LABELS[value as TicketPriority]
    : 'none';
}

function linkWord(value: unknown): string {
  const v = value as { kind?: unknown; ref?: unknown } | null;
  if (!v) return 'a link';
  const ref = typeof v.ref === 'string' ? v.ref : 'a ticket you cannot see';
  if (v.kind === 'duplicate_of') return `duplicate of ${ref}`;
  if (v.kind === 'related') return `related ticket ${ref}`;
  return ref;
}

/** A timeline row in words, after the actor's name ("changed the status from Triage to In progress"). */
export function describeEvent(event: Pick<TicketEvent, 'field' | 'before' | 'after'>): string {
  const { field, before, after } = event;
  switch (field) {
    case 'created':
      return 'filed the ticket';
    case 'escalated':
      return 'escalated the ticket to Trace';
    case 'handed_back':
      return "handed the ticket back to the customer's help desk";
    case 'resolved':
      return 'resolved the ticket';
    case 'reopened':
      return 'reopened the ticket';
    case 'trace_status':
      return `changed the status from ${statusWord(before)} to ${statusWord(after)}`;
    case 'priority':
      return `changed the priority from ${priorityWord(before)} to ${priorityWord(after)}`;
    case 'labels':
      return `changed the labels from ${labelList(before)} to ${labelList(after)}`;
    case 'staff_assignee': {
      const name = personName(after);
      return name
        ? `assigned the ticket to ${name}`
        : after
          ? 'assigned the ticket'
          : 'unassigned the ticket';
    }
    case 'customer_assignee': {
      const name = personName(after);
      return name ? `assigned the help desk side to ${name}` : 'changed the help desk assignee';
    }
    case 'links':
      return after ? `linked ${linkWord(after)}` : `removed the link ${linkWord(before)}`;
    case 'subscribed':
      return after === true ? 'followed the ticket' : 'stopped following the ticket';
    default:
      return `changed ${field.replace(/_/g, ' ')}`;
  }
}
